import React, { useEffect, useState } from 'react';
import { Close } from "@mui/icons-material";
import api from '../../services/api';

interface UsuarioFormProps {
  usuario?: any;
  onClose: () => void;
  onSaved: () => void;
}

const UsuarioForm: React.FC<UsuarioFormProps> = ({ usuario, onClose, onSaved }) => {
  const isEdicao = !!usuario?.uid_usuario;

  const [nome, setNome] = useState(usuario?.nome || '');
  const [email, setEmail] = useState(usuario?.email || '');
  const [telefone, setTelefone] = useState(usuario?.telefone || '');
  const [ativo, setAtivo] = useState(usuario?.ativo !== undefined ? !!usuario.ativo : true);
  const [projetos, setProjetos] = useState<any[]>([]);
  const [projetosSelecionados, setProjetosSelecionados] = useState<string[]>(
    usuario?.projetoVinculos?.map((v: any) => v.projeto?.uid_pesca_projeto).filter(Boolean) || []
  );
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/pesca/projetos').then(res => setProjetos(res.data.data));
  }, []);

  const toggleProjeto = (uid: string) => {
    setProjetosSelecionados(prev =>
      prev.includes(uid) ? prev.filter(p => p !== uid) : [...prev, uid]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || (!isEdicao && !email.trim())) {
      setErro('Nome e e-mail são obrigatórios.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      if (isEdicao) {
        await api.put(`/pesca/usuarios/${usuario.uid_usuario}`, {
          nome: nome.trim(),
          telefone: telefone.trim() || null,
          ativo,
          uid_pesca_projetos: projetosSelecionados,
        });
      } else {
        await api.post('/pesca/usuarios', {
          nome: nome.trim(),
          email: email.trim(),
          uid_pesca_projetos: projetosSelecionados,
        });
      }
      onSaved();
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao salvar usuário.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, color: 'var(--green-700, #00b37e)' }}>
            {isEdicao ? 'Editar Usuário' : 'Novo Usuário'}
          </h2>
          <Close style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Nome *</label>
            <input style={inputStyle} value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>E-mail *{isEdicao ? ' (não pode ser alterado aqui)' : ''}</label>
            <input
              style={inputStyle}
              type="email"
              value={email}
              disabled={isEdicao}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@dominio.com"
            />
          </div>

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Telefone</label>
            <input style={inputStyle} value={telefone} onChange={(e) => setTelefone(e.target.value)} />
          </div>

          {isEdicao && (
            <div style={fieldWrapStyle}>
              <label style={labelStyle}>Status</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
                <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
                {ativo ? 'Ativo' : 'Inativo'}
              </label>
            </div>
          )}

          <div style={fieldWrapStyle}>
            <label style={labelStyle}>Projetos vinculados</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
              {projetos.map((p) => (
                <label key={p.uid_pesca_projeto} style={chipStyle(projetosSelecionados.includes(p.uid_pesca_projeto))}>
                  <input
                    type="checkbox"
                    style={{ marginRight: '6px' }}
                    checked={projetosSelecionados.includes(p.uid_pesca_projeto)}
                    onChange={() => toggleProjeto(p.uid_pesca_projeto)}
                  />
                  {p.nome}
                </label>
              ))}
              {projetos.length === 0 && <span style={{ color: '#999', fontSize: '0.85rem' }}>Nenhum projeto cadastrado.</span>}
            </div>
            <p style={{ fontSize: '0.75rem', color: '#999', marginTop: '6px' }}>
              Nenhum projeto marcado = o usuário não verá dados de nenhum projeto (a menos que seja administrador do sistema).
            </p>
          </div>

          {!isEdicao && (
            <p style={{ fontSize: '0.8rem', color: '#666' }}>
              O usuário receberá um e-mail para definir a própria senha.
            </p>
          )}

          {erro && <p style={{ color: '#f75a68', fontSize: '0.85rem' }}>{erro}</p>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button type="button" onClick={onClose} style={secondaryBtnStyle}>Cancelar</button>
            <button type="submit" disabled={salvando} style={primaryBtnStyle}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px',
  width: '90%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};
const fieldWrapStyle: React.CSSProperties = { marginBottom: '14px' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: '0.8rem', color: '#666', marginBottom: '4px' };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff', cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 20px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer',
};
const chipStyle = (ativo: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', padding: '4px 10px', borderRadius: '16px',
  border: `1px solid ${ativo ? '#03c9fe' : '#ddd'}`, background: ativo ? '#e6f9ff' : '#fff',
  fontSize: '0.85rem', cursor: 'pointer',
});

export default UsuarioForm;
